import { gql } from '@apollo/client';
import { ACCEPT_IMAGE_FILES } from '@mnd-apps/publish/components/MediaUploaders/constants';
import ImageUploader from '@mnd-apps/publish/components/MediaUploaders/ImageUploader';
import { MediaPicker } from '@mnd-apps/publish/components/materials/MediaPicker';
import { useCurrentPressroom } from '@mnd-apps/publish/contexts/CurrentPressroomContext';
import { useImportFromOtherNewsroomMutation } from '@mnd-apps/publish/generated/hooks';
import { useCloudinaryImageUpload } from '@mnd-apps/publish/hooks/useCloudinaryImageUpload';
import { MaterialType } from '@mnd-frontend/graphql-schema';
import { Button } from '@mnd-frontend/ui/publish/components';
import { useToasts } from '@mnd-frontend/ui/publish/contexts';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { UploadQueue } from './utils/UploadQueue';

const wrapperStyles = css<{ $hidden?: boolean }>`
  display: ${({ $hidden }) => ($hidden ? 'none' : 'flex')};
  flex-direction: column;
  > *:first-child {
    flex: 1;
  }
  > *:nth-child(2) {
    margin-top: 0.5rem;
  }
`;

const WrapperAsDiv = styled.div<{ $hidden?: boolean }>`
  ${wrapperStyles}
`;

const WrapperAsLi = styled.li<{ $hidden?: boolean }>`
  ${wrapperStyles}
`;

gql`
  mutation ImportFromOtherNewsroom($input: ImagesImportFromOtherNewsroomInput!) {
    imagesImportFromOtherNewsroom(input: $input) {
      error {
        message
      }
      skippedDuplicates
      images {
        id
        image {
          id
        }
      }
    }
  }
`;

const initialSelect = { value: new Set<string>() };

const InnerImageUploaderCell = ({
  setPendingFiles,
  refetch,
  hidden = false,
  hasImagesInOtherNewsroom = false,
  buttonPortalNode = null,
  asListItem = true,
}: {
  setPendingFiles: React.Dispatch<React.SetStateAction<number>>;
  refetch: () => void;
  hidden?: boolean;
  hasImagesInOtherNewsroom?: boolean;
  buttonPortalNode?: Element | null;
  asListItem?: boolean;
}) => {
  const { showFailureToast, showSuccessToast } = useToasts();
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedIds, setSelectedIds] = useState(initialSelect);
  const { pressroomId } = useCurrentPressroom();
  const uploadQueueRef = useRef<UploadQueue | null>(null);
  const importingCountRef = useRef(0);
  const uploadingCountRef = useRef(0);
  const [t] = useTranslation();

  const [uploadImage, { loading: uploadLoading }] = useCloudinaryImageUpload({
    onError: (error?: string) => showFailureToast(error ?? t('common_general_error')),
    onCompleted: response => {
      const error = response.imageSyncCloudinaryUpload?.error;
      if (error) {
        showFailureToast(error.message || t('common_general_error'));
      }
    },
  });

  const getUploadQueue = useCallback(() => {
    if (uploadQueueRef.current) return uploadQueueRef.current;

    uploadQueueRef.current = new UploadQueue();
    return uploadQueueRef.current;
  }, []);

  const setCorrectPendingFiles = () => {
    setPendingFiles(importingCountRef.current + uploadingCountRef.current);
  };

  const [importImages, { loading: importLoading }] = useImportFromOtherNewsroomMutation({
    onError: () => {
      importingCountRef.current = 0;
      setCorrectPendingFiles();
      showFailureToast(t('common_general_error'));
    },
    onCompleted: response => {
      const error = response.imagesImportFromOtherNewsroom?.error;
      importingCountRef.current = 0;
      setCorrectPendingFiles();
      if (error) {
        showFailureToast(error.message || t('common_general_error'));
      } else {
        const importedCount = response.imagesImportFromOtherNewsroom?.images?.length || 0;
        const skipCount = response.imagesImportFromOtherNewsroom?.skippedDuplicates || 0;
        if (skipCount === 0) {
          showSuccessToast(
            t('media_bank_import_toast_success', {
              count: importedCount,
            }),
          );
        } else if (importedCount > 0) {
          showSuccessToast(
            `${t('media_bank_import_toast_success', {
              count: importedCount,
            })} ${t('media_bank_import_toast_skipped', {
              count: skipCount,
            })}`,
          );
        } else {
          showFailureToast(t('media_bank_import_toast_skipped', { count: skipCount }));
        }
        if (importedCount > 0) {
          refetch();
        }
      }
    },
  });

  useEffect(() => {
    getUploadQueue().updateFunctionPointers({
      upload: uploadImage,
      onStarted: () => setUploadingImage(true),
      onCompleted: () => setUploadingImage(false),
      onFileUploaded: refetch,
      onCountChanged: count => {
        uploadingCountRef.current = count;
        setCorrectPendingFiles();
      },
    });
  });

  useEffect(
    () => () => {
      getUploadQueue().destroy();
    },
    [getUploadQueue],
  );

  const Wrapper = asListItem ? (WrapperAsLi as typeof WrapperAsDiv) : WrapperAsDiv;
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [showMediaModal, setShowMediaModal] = useState(false);

  return (
    <>
      <Wrapper ref={wrapperRef} $hidden={hidden}>
        <ImageUploader
          action={(file: File) => getUploadQueue().queue([file])}
          loading={uploadingImage || uploadLoading || importLoading}
          validatorProps={{ validFileFormats: ACCEPT_IMAGE_FILES }}
          multiple={true}
        />
      </Wrapper>
      {buttonPortalNode &&
        ReactDOM.createPortal(
          <>
            <Button
              type="button"
              disabled={importLoading || uploadingImage}
              onClick={() => {
                wrapperRef.current
                  ?.querySelector<HTMLInputElement>(
                    'input[type="file"][data-is-file-uploader="true"]',
                  )
                  ?.click();
              }}
            >
              {t('media_bank_upload_button')}
            </Button>
            {hasImagesInOtherNewsroom && (
              <>
                <Button
                  onClick={() => {
                    setShowMediaModal(true);
                  }}
                  configuration="text"
                  disabled={importLoading || uploadingImage}
                  type="button"
                >
                  {t('media_bank_import_from_other_newsroom')}
                </Button>
                <MediaPicker
                  show={showMediaModal}
                  onDismiss={() => {
                    if (selectedIds.value.size > 0) {
                      importingCountRef.current = selectedIds.value.size;
                      setCorrectPendingFiles();
                      importImages({
                        variables: {
                          input: {
                            pressroomId,
                            imageIds2: Array.from(selectedIds.value),
                          },
                        },
                      });
                      setSelectedIds({ value: new Set() });
                    }
                    setShowMediaModal(false);
                  }}
                  selectableTypes={[MaterialType.Image]}
                  title={t('publish_pr-edit_image-bank-gallery-title')}
                  searchPlaceholder={t('publish_edit-page_image-bank_search-placeholder')}
                  showUploader={false}
                  onlyOtherPressrooms
                  isSelected={m => m.__typename === 'Image' && selectedIds.value.has(m.id)}
                  onSelect={m => {
                    if (m.__typename !== 'Image' || !m.thumbnail?.resourceImageId) {
                      // Should never happen due to selectableTypes prop
                      return;
                    }
                    setSelectedIds(({ value }) => {
                      if (value.has(m.id)) {
                        value.delete(m.id);
                      } else {
                        value.add(m.id);
                      }
                      return { value };
                    });
                  }}
                />
              </>
            )}
          </>,
          buttonPortalNode,
        )}
    </>
  );
};

export const ImageUploaderCell = React.memo(InnerImageUploaderCell);
